// CoverHistoryModal.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Tooltip,
  message,
  Spin,
  Tag,
  Empty,
  Space,
  Modal as AntdModal,
} from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  CrownOutlined,
  BranchesOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import FeatherSpinner from './components/FeatherSpinner';
import LoadingCard from './components/LoadingCard';
import { useCallback } from 'react';
import './CoverHistoryModal.css';

const { Title, Text } = Typography;

interface HistoryItem {
  id: string;
  language?: string;
  type?: string; // generated | variation | modified | saved
  mode?: 'generate' | 'modify';
  provider?: string;
  model?: string;
  prompt?: string;
  extraPrompt?: string; // user-provided extra prompt for variations
  options?: any;
  createdAt?: string;
  isChosen?: boolean;
  signedThumbUrl?: string | null;
  signedFullUrl?: string | null;
  hasPublicUrl?: boolean;
}

interface CoverHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  apiBase?: string;
  bookId: string;
  userId: string;
  // Language context the user is working in (Original or target language name)
  targetLanguage?: string;
  // Display name for the book's original language (used when targetLanguage is 'Original')
  bookLanguageName?: string;
  bookTitle?: string;
  // Called after a successful "Use this cover" promotion so parent can update UI
  onPromoted?: (languageKey: string, url: string) => void;
  // Open the Cover Variations modal in parent with the chosen image URL
  onOpenVariations?: (imageUrl: string) => void;
}

const API_BASE_DEFAULT = 'https://askpoeai-nswdzfky7q-uc.a.run.app';
//const API_BASE_DEFAULT = 'http://localhost:8000';

function summarizeCoverHistoryItem(item: HistoryItem, displayLanguage: string): {
  modeLabel: string;
  languageLabel: string;
  textSnippet: string | null;
  promptSnippet: string;
  createdLabel: string;
} {
  const opts = item.options || {};
  const modeLabel = item.mode === 'modify'
    ? 'Translated cover'
    : item.type === 'variation'
      ? 'Variation'
      : item.type === 'saved'
        ? 'Saved cover'
        : 'Generated cover';
  const languageLabel = item.language || displayLanguage;
  const textParts = [
    opts.translatedTitle || opts.originalTitle,
    opts.translatedSeries || opts.originalSeries,
    opts.addLanguageSticker ? `[${languageLabel} Edition]` : '',
  ].filter(Boolean);
  const textSnippet = textParts.length ? textParts.join(' / ') : null;
  const promptText = item.prompt || item.extraPrompt || opts.prompt || '';
  const promptSnippet = promptText
    ? String(promptText).replace(/\s+/g, ' ').trim()
    : 'No prompt saved';
  const createdLabel = item.createdAt ? new Date(item.createdAt).toLocaleString() : 'Date unavailable';
  return { modeLabel, languageLabel, textSnippet, promptSnippet, createdLabel };
}

function normalizeLanguageForApi(language?: string): string {
  if (!language) return 'original';
  const key = (language || '').trim();
  if (key.toLowerCase() === 'original') return 'original';
  return key;
}

const CoverHistoryModal: React.FC<CoverHistoryModalProps> = ({
  visible,
  onClose,
  apiBase = API_BASE_DEFAULT,
  bookId,
  userId,
  targetLanguage,
  bookLanguageName,
  bookTitle = 'cover',
  onPromoted,
  onOpenVariations,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<HistoryItem | null>(null);
  const [previewSticker, setPreviewSticker] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState<string>("");
  const [previewImgLoading, setPreviewImgLoading] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  // Pagination state for lazy loading
  const [page, setPage] = useState(1);
  const [pageSize] = useState(48);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [openingVariationsId, setOpeningVariationsId] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [activeCoverUrl, setActiveCoverUrl] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [viewHeight, setViewHeight] = useState<number>(0);
  const [tourOpen, setTourOpen] = useState<boolean>(false);
  const isTourTargetsReady = useCallback(() => {
    const el = infoRowRef.current || firstCardRef.current;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect && rect.width > 0 && rect.height > 0;
  }, []);

  const openTour = useCallback(() => {
    if (loading || loadingActive || promotingId !== null) return;
    const attempt = (tries: number) => {
      if (loading || loadingActive || promotingId !== null) return;
      if (isTourTargetsReady()) {
        setTimeout(() => setTourOpen(true), 0);
      } else if (tries > 0) {
        setTimeout(() => attempt(tries - 1), 100);
      }
    };
    attempt(10);
  }, [loading, loadingActive, promotingId, isTourTargetsReady]);
  // Refs for Tour targets
  const infoRowRef = useRef<HTMLDivElement | null>(null);
  const firstCardRef = useRef<HTMLDivElement | null>(null);
  const activeTagRef = useRef<HTMLSpanElement | null>(null);
  const previewBtnRef = useRef<HTMLButtonElement | null>(null);
  const promoteBtnRef = useRef<HTMLButtonElement | null>(null);
  const downloadBtnRef = useRef<HTMLButtonElement | null>(null);
  const variationsBtnRef = useRef<HTMLButtonElement | null>(null);
  const deleteBtnRef = useRef<HTMLButtonElement | null>(null);

  const normalizedLanguage = useMemo(() => normalizeLanguageForApi(targetLanguage), [targetLanguage]);
  const displayLanguage = normalizedLanguage === 'original'
    ? (bookLanguageName || 'Original')
    : normalizedLanguage;

  // Resolve sticker text for preview on demand
  useEffect(() => {
    let cancelled = false;
    const it = previewItem;
    if (!it) { setPreviewSticker(null); return; }
    const opts: any = it.options || {};
    const needSticker = (it.mode === 'modify') && !!opts.addLanguageSticker && (it.language || '').toLowerCase() !== 'original';
    if (!needSticker) { setPreviewSticker(null); return; }
    const lang = it.language || '';
    (async () => {
      try {
        const res = await fetch(`${apiBase || API_BASE_DEFAULT}/api/cover/sticker-text?language=${encodeURIComponent(lang)}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!cancelled) setPreviewSticker((data && data.stickerText) || `${lang} Edition`);
      } catch {
        if (!cancelled) setPreviewSticker(`${lang} Edition`);
      }
    })();
    return () => { cancelled = true; };
  }, [previewItem, apiBase]);

  const sanitizeFilename = (s: string) =>
    (s || '').replace(/[^a-zA-Z0-9\-_. ]/g, '').trim().replace(/\s+/g, '_');

  const fetchPage = async (p: number) => {
    if (!visible) return;
    if (p === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const params = new URLSearchParams();
      params.set('userId', userId);
      if (normalizedLanguage) params.set('language', normalizedLanguage);
      params.set('page', String(p));
      params.set('pageSize', String(pageSize));
      const url = `${apiBase}/api/book/${encodeURIComponent(bookId)}/covers/history?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const arr: HistoryItem[] = (data?.items || []) as any[];
      setItems((prev) => (p === 1 ? arr : [...prev, ...arr]));
      const serverHasMore = typeof data?.hasMore === 'boolean' ? data.hasMore : arr.length === pageSize;
      setHasMore(serverHasMore);
      setPage(p);
    } catch (e: any) {
      console.error('Failed to load cover history', e);
      message.error('Failed to load cover history');
    } finally {
      if (p === 1) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const handleDelete = async (it: HistoryItem) => {
    AntdModal.confirm({
      title: 'Delete this cover from history?',
      icon: <ExclamationCircleOutlined className="modal-token-icon modal-token-icon--warning" />,
      content: 'This will remove the image from your history. This does not remove your current active cover for this language.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingId(it.id);
        try {
          const url = `${apiBase}/api/book/${encodeURIComponent(bookId)}/covers/history/${encodeURIComponent(it.id)}`;
          const res = await fetch(url, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId }),
          });
          if (!res.ok) throw new Error(await res.text());
          // Remove from list locally
          setItems(prev => prev.filter(x => x.id !== it.id));
          message.success('Deleted from history');
        } catch (e: any) {
          console.error('Delete failed', e);
          message.error('Failed to delete');
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const fetchHistory = async () => {
    // reset and load first page
    setItems([]);
    setHasMore(true);
    setPage(1);
    await fetchPage(1);
  };

  const fetchActiveCover = async () => {
    if (!visible) return;
    // Only include active cover for non-original languages
    if (normalizedLanguage === 'original') {
      setActiveCoverUrl(null);
      return;
    }
    setLoadingActive(true);
    try {
      const url = `${apiBase}/api/book/${encodeURIComponent(bookId)}/covers`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const covers = (data?.covers || {}) as Record<string, string>;
      const key = (normalizedLanguage || '').toLowerCase();
      const current = covers[key] || null;
      setActiveCoverUrl(current || null);
    } catch (_e) {
      setActiveCoverUrl(null);
    } finally {
      setLoadingActive(false);
    }
  };

  useEffect(() => {
    if (visible) {
      void fetchHistory();
      void fetchActiveCover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, normalizedLanguage, refreshKey]);

  const handleDownload = async (it: HistoryItem) => {
    try {
      const filenameBase = sanitizeFilename(bookTitle || 'cover');
      const langSuffix = normalizedLanguage === 'original' ? 'Original' : normalizedLanguage;
      const itemSuffix = it.id === '__active__' ? 'active' : it.id;
      const jpegFilenameBase = `${filenameBase}_${langSuffix}_${itemSuffix}`;
      let sourceUrl = it.signedFullUrl || it.signedThumbUrl || '';

      if (it.id !== '__active__') {
        const historyUrl = `${apiBase}/api/book/${encodeURIComponent(bookId)}/covers/history/${encodeURIComponent(it.id)}/download`;
        const historyRes = await fetch(historyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, filename: `${jpegFilenameBase}.png` }),
        });
        if (!historyRes.ok) throw new Error(await historyRes.text());
        const data = await historyRes.json();
        sourceUrl = data?.signedUrl || sourceUrl;
      }

      if (!sourceUrl) throw new Error('Download URL unavailable');

      const jpegRes = await fetch(`${apiBase}/api/proxy-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: sourceUrl,
          filename: jpegFilenameBase,
          output_format: 'jpeg',
        }),
      });
      if (!jpegRes.ok) throw new Error(await jpegRes.text());
      const contentType = jpegRes.headers.get('content-type') || '';
      if (!contentType.toLowerCase().includes('image/jpeg')) {
        throw new Error(`Expected JPEG data, received ${contentType || 'an unknown format'}`);
      }

      const blob = await jpegRes.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${jpegFilenameBase}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      message.success('JPEG downloaded');
    } catch (e: any) {
      console.error('Download failed', e);
      message.error('Failed to download JPEG');
    }
  };

  const handlePromote = async (it: HistoryItem) => {
    try {
      setPromotingId(it.id);
      const url = `${apiBase}/api/book/${encodeURIComponent(bookId)}/covers/history/${encodeURIComponent(it.id)}/promote`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const coverUrl = data?.coverUrl;
      if (coverUrl && onPromoted) {
        const key = normalizedLanguage === 'original' ? 'original' : normalizedLanguage;
        onPromoted(key, coverUrl);
      }
      // Refresh active cover and history without clearing first, to avoid flicker
      await Promise.all([
        fetchActiveCover(),
        fetchPage(1),
      ]);
      message.success('Cover set as active');
    } catch (e: any) {
      console.error('Promote failed', e);
      message.error('Failed to set as active');
    } finally {
      setPromotingId(null);
    }
  };

  // Preserve server ordering (newest-first) from server for pagination basis.
  // Compute a single active item and pin it to the top for display while preventing duplicates.
  const activeItem = useMemo(() => {
    if (!items || items.length === 0) return undefined;
    // Prefer the first isChosen (newest-first from server)
    return items.find((it) => it.isChosen);
  }, [items]);

  const displayItems = useMemo(() => {
    const base = items || [];
    if (base.length === 0 && activeCoverUrl && normalizedLanguage !== 'original') {
      // If no history yet but an active cover exists for this language, show it alone
      return [{
        id: '__active__',
        language: normalizedLanguage,
        type: 'saved',
        isChosen: true,
        signedThumbUrl: activeCoverUrl,
        signedFullUrl: activeCoverUrl,
        hasPublicUrl: true,
      } as HistoryItem];
    }

    if (activeItem) {
      const rest = base.filter((it) => it.id !== activeItem.id);
      return [activeItem, ...rest];
    }

    if (activeCoverUrl && normalizedLanguage !== 'original') {
      const synthetic: HistoryItem = {
        id: '__active__',
        language: normalizedLanguage,
        type: 'saved',
        isChosen: true,
        signedThumbUrl: activeCoverUrl,
        signedFullUrl: activeCoverUrl,
        hasPublicUrl: true,
      };
      return [synthetic, ...base];
    }
    return base;
  }, [items, activeItem, activeCoverUrl, normalizedLanguage]);

  // Determine which card to target in the Tour for action buttons:
  // If there is an active card and at least one other item, use the newest non-active item.
  // Otherwise, use the first (which may be the active card or the only card).
  const activeCardId = useMemo(() => {
    if (activeItem) return activeItem.id;
    if (activeCoverUrl && normalizedLanguage !== 'original') return '__active__';
    return null;
  }, [activeItem, activeCoverUrl, normalizedLanguage]);

  const tourTargetIndex = useMemo(() => {
    if (!displayItems || displayItems.length === 0) return -1;
    const idxNonActive = displayItems.findIndex((it) => it.id !== activeCardId);
    return idxNonActive >= 0 ? idxNonActive : 0;
  }, [displayItems, activeCardId]);

  // Infinite scroll observer
  useEffect(() => {
    if (!visible || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && !loadingMore && !loading) {
        void fetchPage(page + 1);
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [visible, hasMore, loadingMore, loading, page, normalizedLanguage, refreshKey]);

  // Measure the visible height of the modal body to center the spinner within the current view
  useEffect(() => {
    if (!visible) return;
    const update = () => {
      try {
        const el = bodyRef.current;
        const parent = el ? (el.parentElement as HTMLElement | null) : null;
        const h = parent?.clientHeight || el?.clientHeight || 0;
        setViewHeight(h);
      } catch (_e) {}
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [visible, loading, loadingActive, promotingId]);

  // Auto-open tour on first visit (persisted flag in DB), but only after modal is fully open and not loading
  useEffect(() => {
    if (!visible) return;
    const FLAG = 'tourSeen_coverHistory';
    let cancelled = false;
    const load = async () => {
      try {
        // Ask backend for user flags
        const res = await fetch(`${apiBase}/api/users/${encodeURIComponent(userId)}/flags?keys=${encodeURIComponent(FLAG)}`);
        if (!res.ok) return;
        const data = await res.json();
        const seen = !!data?.[FLAG];
        if (seen) return;
        // Defer opening until layout is stable and not loading
        const tryOpen = () => {
          if (cancelled) return;
          if (loading || loadingActive || promotingId !== null) {
            // Retry shortly until stable
            setTimeout(tryOpen, 100);
            return;
          }
          requestAnimationFrame(() => {
            if (cancelled) return;
            if (!isTourTargetsReady()) {
              setTimeout(tryOpen, 100);
              return;
            }
            openTour();
            // Persist flag
            fetch(`${apiBase}/api/users/${encodeURIComponent(userId)}/flags/${FLAG}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ value: true }),
            }).catch(() => {});
          });
        };
        tryOpen();
      } catch (_e) {}
    };
    // Slight delay to ensure modal afterOpen animations/layout complete
    const t = setTimeout(load, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [visible, apiBase, userId, openTour, loading, loadingActive, promotingId, isTourTargetsReady]);

  // Ensure Tour is closed when modal closes
  useEffect(() => {
    if (!visible) setTourOpen(false);
  }, [visible]);

  const hasActiveDisplayItem = Boolean(activeItem || displayItems.some((it) => it.id === '__active__' || it.isChosen));

  const titleNode = (
    <div className="cover-history-title">
      <div className="cover-history-title__heading">
        <Title level={4} style={{ margin: 0 }}>
          Cover History - {displayLanguage}
        </Title>
      </div>

      <div ref={infoRowRef as any} className="cover-history-title__info">
        <Tag className="modal-token-tag modal-token-tag--info">Language: {displayLanguage}</Tag>
        <Tooltip title="Click an image to preview. Use the icons to set active, download, or create variations.">
          <InfoCircleOutlined className="cover-history-title__info-icon" />
        </Tooltip>
      </div>
      <div className="cover-history-title__tour">
        <Button type="link" size="small" icon={<QuestionCircleOutlined />} onClick={openTour} className="app-tour-trigger">
          Take a Tour
        </Button>
      </div>
    </div>
  );

  const overlayLabel = promotingId !== null
    ? 'Setting active cover...'
    : openingVariationsId !== null
      ? 'Opening variations...'
      : loadingActive
        ? 'Loading active cover...'
        : 'Loading cover history...';

  return (
    <Modal
      title={titleNode}
      open={visible}
      onCancel={onClose}
      footer={
        <div className="cover-history-footer">
	          <div className="cover-history-footer__summary" aria-live="polite">
	            <strong>{hasActiveDisplayItem ? 'Active cover selected' : 'No active history item loaded'}</strong>
            <span>
              {displayItems.length} saved cover{displayItems.length === 1 ? '' : 's'} for {displayLanguage}.
              {' '}Setting a cover active replaces what readers see for this language.
            </span>
          </div>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => setRefreshKey((k) => k + 1)} disabled={loading || loadingActive}>
              Refresh
            </Button>
            <Button onClick={onClose}>Close</Button>
          </Space>
        </div>
      }
      width="min(1200px, 90vw)"
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}
      className="cover-history-modal"
      destroyOnClose
      closable={false}
      centered
    >
	      <div ref={bodyRef} className="cover-history-body" aria-busy={loading || loadingActive || promotingId !== null || openingVariationsId !== null} data-testid="tutorial:cover:history-modal">
        {(loading || loadingActive || promotingId !== null || openingVariationsId !== null) && (
          <div style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <div
              className="cover-history-loading-panel"
              style={{
                height: (viewHeight || 0) > 0 ? viewHeight : '60vh',
              }}
            >
              <LoadingCard
                label={overlayLabel}
                className="cover-history-loading-card"
                indicator={<FeatherSpinner style={{ width: 128, height: 128 }} />}
              />
            </div>
          </div>
        )}
	        <div className="cover-history-grid-shell" data-testid="tutorial:cover:history-list" style={{ pointerEvents: (loading || loadingActive || promotingId !== null || openingVariationsId !== null) ? 'none' : 'auto' }}>
          {(!displayItems || displayItems.length === 0) ? (
            <div style={{ padding: 24 }}>
              <Empty description="No cover history yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <>
              {/* Language tag and info icon moved to header (left side) */}

              <Row gutter={[24, 16]} justify="center">
                {displayItems.map((it, idx) => (
                <Col key={it.id} xs={24} sm={12} md={8} lg={6}>
	                  <Card
	                    hoverable
	                    bodyStyle={{ padding: 8 }}
	                    data-testid={idx === 0 ? 'tutorial:cover:history-card' : undefined}
	                    style={{
                      borderRadius: 8,
                      backgroundColor: 'var(--surface-muted)',
                      transition: 'box-shadow 0.2s ease',
                      overflow: 'hidden',
                    }}
                    ref={idx === tourTargetIndex ? (firstCardRef as any) : undefined}
                    onClick={() => {
                      setPreviewItem(it);
                      setPreviewImgLoading(true);
                      setPreviewKey(`${it.id}:${Date.now()}`);
                      setPreviewUrl(it.signedFullUrl || it.signedThumbUrl || null);
                    }}
                    cover={
                      <div style={{ position: 'relative', padding: 8, display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
                          {((activeItem && it.id === activeItem.id) || (!activeItem && it.id === '__active__')) && (
                            <Tag
                              color="success"
                              style={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }}
                          ref={idx === 0 ? (activeTagRef as any) : undefined}
                            >
                              Active
                            </Tag>
                          )}
                        <img
                          alt="cover history"
                          src={it.signedThumbUrl || it.signedFullUrl || ''}
                          loading="lazy"
                          decoding="async"
                          style={{
                            width: '100%',
                            height: 280,
                            objectFit: 'contain',
                            borderRadius: 4,
                            display: 'block',
                          }}
                        />
                      </div>
                    }
                    actions={[
                      <Tooltip title="Preview" key="preview">
                        <Button
                          type="text"
                          shape="circle"
                          disabled={!(it.signedFullUrl || it.signedThumbUrl)}
                          icon={<EyeOutlined />}
	                          aria-label="Preview"
	                          data-testid={idx === 0 ? 'tutorial:cover:history-preview' : undefined}
	                          ref={idx === tourTargetIndex ? (previewBtnRef as any) : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewItem(it);
                            setPreviewImgLoading(true);
                            setPreviewKey(`${it.id}:${Date.now()}`);
                            setPreviewUrl(it.signedFullUrl || it.signedThumbUrl || null);
                          }}
                        />
                      </Tooltip>,
                      <Tooltip title="Set as active" key="use">
                        <Button
                          type="text"
                          shape="circle"
                          disabled={it.id === '__active__' || (activeItem && it.id === activeItem.id)}
                          loading={promotingId === it.id}
                          icon={<CrownOutlined />}
	                          aria-label="Set as active"
	                          data-testid={idx === 0 ? 'tutorial:cover:history-set-active' : undefined}
	                          ref={idx === tourTargetIndex ? (promoteBtnRef as any) : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handlePromote(it);
                          }}
                        />
                      </Tooltip>,
                      <Tooltip title="Download JPEG" key="download">
                        <Button
                          type="text"
                          shape="circle"
                          disabled={!(it.signedFullUrl || it.signedThumbUrl) && it.id === '__active__'}
                          icon={<DownloadOutlined />}
	                          aria-label="Download JPEG"
	                          data-testid={idx === 0 ? 'tutorial:cover:history-download' : undefined}
	                          ref={idx === tourTargetIndex ? (downloadBtnRef as any) : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDownload(it);
                          }}
                        />
                      </Tooltip>,
                      <Tooltip title="Variations" key="variations">
                        <Button
                          type="text"
                          shape="circle"
                          icon={<BranchesOutlined />}
                          disabled={!(it.signedFullUrl || it.signedThumbUrl) || !onOpenVariations}
                          loading={openingVariationsId === it.id}
	                          aria-label="Variations"
	                          data-testid={idx === 0 ? 'tutorial:cover:history-variations' : undefined}
	                          ref={idx === tourTargetIndex ? (variationsBtnRef as any) : undefined}
                          onClick={async (e) => {
                            e.stopPropagation();
                            const url = it.signedFullUrl || it.signedThumbUrl || '';
                            if (!url || !onOpenVariations) return;
                            try {
                              setOpeningVariationsId(it.id);
                              await Promise.resolve(onOpenVariations(url));
                            } finally {
                              setOpeningVariationsId(null);
                            }
                          }}
                        />
                      </Tooltip>,
                      <Tooltip title="Delete from history" key="delete">
                        <Button
                          type="text"
                          shape="circle"
                          danger
                          loading={deletingId === it.id}
                          disabled={it.id === '__active__'}
                          icon={<DeleteOutlined />}
                          aria-label="Delete"
                          ref={idx === tourTargetIndex ? (deleteBtnRef as any) : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDelete(it);
                          }}
                        />
                      </Tooltip>,
                    ]}
	                  >
	                    {(() => {
	                      const summary = summarizeCoverHistoryItem(it, displayLanguage);
	                      return (
	                        <div className="cover-history-card-meta">
	                          <div className="cover-history-card-meta__row">
	                            <Tag className="modal-token-tag modal-token-tag--compact modal-token-tag--info">
	                              {summary.modeLabel}
	                            </Tag>
	                            <span>{summary.languageLabel}</span>
	                          </div>
	                          {summary.textSnippet ? (
	                            <div className="cover-history-card-meta__line">
	                              {summary.textSnippet}
	                            </div>
	                          ) : null}
	                          <div className="cover-history-card-meta__line cover-history-card-meta__line--muted">
	                            {summary.promptSnippet}
	                          </div>
	                          <div className="cover-history-card-meta__date">
	                            {summary.createdLabel}
	                          </div>
	                        </div>
	                      );
	                    })()}
	                    </Card>
                  </Col>
                ))}
              </Row>
              {hasMore && (
                <div ref={sentinelRef} style={{ height: 1 }} />
              )}
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Spin size="small" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Full-size preview modal */}
      <Modal
        open={!!previewUrl}
        footer={null}
        onCancel={() => { setPreviewUrl(null); setPreviewItem(null); setPreviewSticker(null); }}
        width={1100}
        bodyStyle={{ padding: 0 }}
        className="cover-history-preview-modal"
      >
        <div className="cover-history-preview">
          <div className="cover-history-preview__image">
            {previewImgLoading && (
              <div
                className="cover-history-preview__loading"
              >
                <LoadingCard
                  label="Loading preview..."
                  indicator={<FeatherSpinner style={{ width: 96, height: 96 }} />}
                />
              </div>
            )}
            <img
              key={previewKey}
              alt="cover preview"
              src={previewUrl || ''}
              onLoad={() => setPreviewImgLoading(false)}
              onError={() => setPreviewImgLoading(false)}
              style={{ maxWidth: '100%', maxHeight: '80vh', visibility: previewImgLoading ? 'hidden' : 'visible' }}
            />
          </div>
          <div className="cover-history-preview__details">
            <Title level={5} style={{ marginTop: 0 }}>Details</Title>
            {previewItem && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 8 }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => void handleDownload(previewItem)}
                >
                  Download JPEG
                </Button>
                <div>
                  <Text type="secondary">Language:</Text>
                  <div>{previewItem.language || 'original'}</div>
                </div>
                {previewItem.type === 'generated' && previewItem.prompt && (
                  <div>
                    <Text strong>Prompt</Text>
                    <div className="cover-history-preview__prompt">
                      {previewItem.prompt}
                    </div>
                  </div>
                )}
                {previewItem.type === 'variation' && (previewItem as any).extraPrompt && (
                  <div>
                    <Text strong>Extra Prompt</Text>
                    <div className="cover-history-preview__prompt">
                      {(previewItem as any).extraPrompt}
                    </div>
                  </div>
                )}
                {(() => {
                  const opts: any = previewItem?.options || {};
                  const showTranslated = previewItem?.mode === 'generate' || previewItem?.mode === 'modify';
                  if (!showTranslated) return null;
                  const hasTitle = !!(opts.translatedTitle || opts.originalTitle);
                  const hasSeries = !!(opts.translatedSeries || opts.originalSeries);
                  const needSticker = !!opts.addLanguageSticker && (previewItem?.language || '').toLowerCase() !== 'original';
                  return (
                    <div>
                      {(hasTitle || hasSeries || needSticker) && (<Text strong>On-cover Text</Text>)}
                      {hasTitle && (
                        <div style={{ marginTop: 4 }}>
                          {opts.originalTitle && (<div><Text type="secondary">Original Title:</Text> <span>{opts.originalTitle}</span></div>)}
                          {opts.translatedTitle && (<div><Text type="secondary">Translated Title:</Text> <span>{opts.translatedTitle}</span></div>)}
                        </div>
                      )}
                      {hasSeries && (
                        <div style={{ marginTop: 4 }}>
                          {opts.originalSeries && (<div><Text type="secondary">Original Series:</Text> <span>{opts.originalSeries}</span></div>)}
                          {opts.translatedSeries && (<div><Text type="secondary">Translated Series:</Text> <span>{opts.translatedSeries}</span></div>)}
                        </div>
                      )}
                      {Array.isArray(opts.additionalReplacements) && opts.additionalReplacements.map((row: { source: string; replacement: string }, index: number) => (
                        <div key={`additional-${index}`} style={{ marginTop: 4 }}>
                          <Text type="secondary">Additional text:</Text> <span>{row.source} → {row.replacement}</span>
                        </div>
                      ))}
                      {needSticker && (
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary">Sticker:</Text> <span>[{previewSticker || '…'}]</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Guided Tour */}
      <AppTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={(() => {
          const mk = (ref: React.RefObject<HTMLElement>, title: string, description: React.ReactNode) =>
            (ref.current ? ({ target: () => ref.current as any, title, description } as any) : null);
          const steps = [
            mk(
              infoRowRef as any,
              'Language',
              <span>
                You’re viewing the cover history for <strong>{displayLanguage}</strong>. Each language keeps its
                own history and its own active cover.
              </span>
            ),
            mk(
              activeTagRef as any,
              'Active Cover',
              'The green “Active” banner marks the cover currently published for this language.'
            ),
            mk(
              firstCardRef as any,
              'History Cards',
              'Newest first. Click the image to preview, or use the actions below each card to compare options.'
            ),
            displayItems.length === 0
              ? mk(
                  bodyRef as any,
                  'No history yet',
                  'Generated, translated, and saved covers for this language will appear here after you create or select them.'
                )
              : null,
            mk(
              promoteBtnRef as any,
              'Set Active',
              <span>
                Make this the active <strong>{displayLanguage}</strong> cover. You can switch again any time.
              </span>
            ),
            mk(
              variationsBtnRef as any,
              'Variations',
              'Generate new options inspired by this cover. Successful generations are saved here.'
            ),
            mk(
              previewBtnRef as any,
              'Preview',
              'Open a full-size view to inspect details before deciding.'
            ),
            mk(
              downloadBtnRef as any,
              'Download JPEG',
              'Save a properly converted JPEG image to your device.'
            ),
            mk(
              deleteBtnRef as any,
              'Delete',
              'Remove this item from your history. If it is active, the published cover for this language stays in place until you choose another one.'
            ),
          ].filter(Boolean) as any[];
          // Reorder function-button steps to: View, Set Active, Download, Variations, Delete
          const headTitles = new Set(['Language', 'Active Cover', 'History Cards']);
          const head = steps.filter((s: any) => s && headTitles.has(s.title));
          const findTitle = (title: string, alt?: string) => steps.find((s: any) => s && (s.title === title || (alt && s.title === alt)));
          const viewStep = findTitle('View', 'Preview');
          if (viewStep && viewStep.title === 'Preview') {
            // Normalize title so we don't present duplicate View/Preview entries
            viewStep.title = 'View';
          }
          const orderedFuncs = [
            viewStep,
            findTitle('Set Active'),
            findTitle('Download JPEG'),
            findTitle('Variations'),
            findTitle('Delete'),
          ].filter(Boolean) as any[];
          const used = new Set([ ...head.map((s: any) => s), ...orderedFuncs.map((s: any) => s) ]);
          const rest = steps.filter((s: any) => s && !used.has(s));
          const ordered = [ ...head, ...orderedFuncs, ...rest ];
          return ordered.map((s: any) => ({
            ...s,
            description: renderAppTourPanel(s.description, { ravenSize: 'lg' }),
          }));
        })()}
      />
    </Modal>
  );
};

export default CoverHistoryModal;
